import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * HeroSlider — Editorial split-layout slider with prev/next arrow controls
 *
 * Performance:
 *   - Photos are contained (~1000px wide) — much lighter than full-bleed backgrounds
 *   - Unsplash `auto=format` serves AVIF/WebP automatically
 *   - First slide: fetchpriority="high"; later slides: loading="lazy"
 *   - Tiny blurred placeholder paints instantly, then fades to full photo
 *   - Next slide image is preloaded so transitions are instant
 *
 * Controls:
 *   - Big circular prev/next buttons overlaid on the image (desktop)
 *     + sticky pair above the controls bar on mobile
 *   - Thumbnail indicators in the bottom bar
 *   - Keyboard arrow keys
 *   - Pause on hover
 *
 * Usage: <HeroSlider />
 */

// Non-translatable slide metadata (images, colors, stat keys)
const slideKeys = [
  "customizedSoftware",
  "visitorManagement",
  "mobileApps",
  "erpAutomation",
];

const slideMeta = [
  {
    image: "https://ik.imagekit.io/sy1vtylhnq/mesoft.ae/slider/hero/team-explain",
    imageBlur: "https://ik.imagekit.io/sy1vtylhnq/mesoft.ae/slider/hero/team-explain",
    accent: "#1b4a82",
    statKeys: ["yearsInUae", "projectsDelivered", "regionalReach"],
  },
  {
    image: "https://ik.imagekit.io/sy1vtylhnq/mesoft.ae/slider/hero/g-ivisita.png",
    imageBlur: "https://ik.imagekit.io/sy1vtylhnq/mesoft.ae/slider/hero/g-ivisita.png",
    accent: "#0a2a4f",
    statKeys: ["kiosk", "mobile", "web"],
  },
  {
    image: "https://ik.imagekit.io/sy1vtylhnq/mesoft.ae/slider/hero/mobile.png",
    imageBlur: "https://ik.imagekit.io/sy1vtylhnq/mesoft.ae/slider/hero/mobile.png",
    accent: "#2daf47",
    statKeys: ["ios", "android", "flutter"],
  },
  {
    image: "https://ik.imagekit.io/sy1vtylhnq/mesoft.ae/slider/hero/logestic.png",
    imageBlur: "https://ik.imagekit.io/sy1vtylhnq/mesoft.ae/slider/hero/logestic.png",
    accent: "#1F7A5A",
    statKeys: ["uptime", "soa", "saas"],
  },
];

export default function HeroSlider() {
  const [index, setIndex] = useState(0);
  const [imgLoaded, setImgLoaded] = useState({});
  const [isPaused, setIsPaused] = useState(false);
  const { t } = useTranslation();

  // Build translated slides from JSON keys
  const slides = useMemo(
    () =>
      slideKeys.map((key, i) => {
        const meta = slideMeta[i];
        const base = `heroSlider.slides.${key}`;
        return {
          number: t(`${base}.number`),
          category: t(`${base}.category`),
          headline: [
            t(`${base}.headline.line1`),
            t(`${base}.headline.line2`),
            t(`${base}.headline.line3`),
            t(`${base}.headline.line4`),
          ],
          italicWord: t(`${base}.italicWord`),
          body: t(`${base}.body`),
          stats: meta.statKeys.map((sk) => ({
            value: t(`${base}.stats.${sk}.value`),
            label: t(`${base}.stats.${sk}.label`),
          })),
          image: meta.image,
          imageBlur: meta.imageBlur,
          imageAlt: t(`${base}.imageAlt`),
          imageCaption: t(`${base}.imageCaption`),
          accent: meta.accent,
        };
      }),
    [t]
  );

  const next = useCallback(
    () => setIndex((i) => (i + 1) % slides.length),
      [slides.length]
  );
  const prev = useCallback(
    () => setIndex((i) => (i - 1 + slides.length) % slides.length),
    [slides.length]
  );
  const goTo = useCallback((i) => setIndex(i), []);

  // Auto-advance
  useEffect(() => {
    if (isPaused) return;
    const id = setInterval(next, 3000);
    return () => clearInterval(id);
  }, [isPaused, next]);

  // Preload next slide image
  useEffect(() => {
    const nextIndex = (index + 1) % slides.length;
    const img = new Image();
    img.src = slides[nextIndex].image;
  }, [index, slides]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);
  const active = slides[index];

  return (
    <section
      className="relative w-full overflow-hidden text-stone-900"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      aria-roledescription="carousel"
    >
      {/* Decorative diagonal line */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: "linear-gradient(to bottom right, transparent 49.8%, rgba(0,0,0,0.05) 49.9%, rgba(0,0,0,0.05) 50.1%, transparent 50.2%)",
        }}
      />
      <div className="relative md:w-theme-container w-mobile-container mx-auto 2xl:mt-1 grid grid-cols-1 xl:grid-cols-2">
        {/* LEFT: Content */}
        <div className="flex flex-col justify-between broke-px-6 pb-5 pt-8 md:pt-10 lg:min-h-[720px] lg:pb-5">
          <div
            key={`top-${index}`}
            className="flex items-baseline justify-between animate-[fadeSlide_0.6s_ease-out]">
            <div className="flex items-center gap-3">
              <span
                className="h-2 w-2 rounded-full"
                style={{ background: active.accent }}
              />
              <span className="text-xs font-medium uppercase tracking-[0.2em] text-stone-600">
                {active.category}
              </span>
            </div>
            <span
              className="font-serif text-sm italic text-stone-500"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {active.number} / 0{slides.length}
            </span>
          </div>
          <div className="my-8 md:my-10">
            <h1 className="font-serif text-[44px] font-light leading-[0.95] tracking-[-0.02em] text-stone-900 md:text-[72px] lg:text-[88px]">
              {active.headline.map((line, i) => {
                const hasItalic = line.includes(active.italicWord);
                return (
                  <h1
                    key={`${index}-${i}`}
                    className="block opacity-0 animate-[fadeSlide_0.7s_ease-out_forwards]"
                    style={{ animationDelay: `${i * 90}ms` }}
                  >
                    {hasItalic ? (
                      <>
                        {line.split(active.italicWord)[0]}
                        <em
                          className="font-serif italic"
                          style={{ color: active.accent }}
                        >
                          {active.italicWord}
                        </em>
                        {line.split(active.italicWord)[1]}
                      </>
                    ) : (
                      line
                    )}
                  </h1>
                );
              })}
            </h1>
            <p
              key={`body-${index}`}
              className="mt-8 w-[80%] text-base leading-relaxed text-stone-600 opacity-0 animate-[fadeSlide_0.7s_ease-out_0.5s_forwards] md:text-lg">
              {active.body}
            </p>
            <div
              key={`cta-${index}`}
              className="mt-10 flex flex-wrap items-center gap-3 opacity-0 animate-[fadeSlide_0.7s_ease-out_0.7s_forwards]"
            >
              <Link
                to={"/contact"}
                className="group inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium text-white transition-all hover:gap-3"
                style={{ background: active.accent }}
              >
                {t("heroSlider.common.startProject")}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M7 7h10v10" />
                  <path d="M7 17 17 7" />
                </svg>
              </Link>
              <a
                href="tel:+97142829613"
                className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-6 py-3 text-sm font-medium text-stone-900 transition-all hover:border-stone-900 hover:bg-stone-900 hover:text-white"
              >
                {t("heroSlider.common.callNow")}
              </a>
            </div>
          </div>
          <div
            key={`stats-${index}`}
            className="w-[90%] grid grid-cols-3 gap-6 border-t border-stone-300 pt-6 opacity-0 animate-[fadeSlide_0.7s_ease-out_0.9s_forwards]"
          >
            {active.stats.map((stat, i) => (
              <div key={i}>
                <h1 className="font-serif text-2xl font-light text-stone-900 md:text-3xl">
                  {stat.value}
                </h1>
                <div className="mt-1 text-xs uppercase tracking-wider text-stone-500">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* RIGHT: Image with overlaid arrow buttons */}
        <div className="relative hidden xl:block py-10 px-4 xl:py-10 ">
          <div className="group/image relative aspect-[4/5] overflow-hidden rounded-2xl bg-stone-200 shadow-2xl shadow-stone-400/30 lg:aspect-auto lg:h-full ">
            {slides.map((s, i) => (
              <div
                key={s.image}
                className="absolute inset-0 transition-opacity duration-700 !rounded-2xl"
                style={{ opacity: i === index ? 1 : 0 }} >
                <img
                  src={s.imageBlur}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 h-full w-full scale-110 object-cover blur-xl !rounded-2xl"
                />
                <img
                  src={s.image}
                  alt={s.imageAlt}
                  loading={i === 0 ? "eager" : "lazy"}
                  fetchpriority={i === 0 ? "high" : "low"}
                  decoding="async"
                  onLoad={() =>
                    setImgLoaded((prev) => ({ ...prev, [s.image]: true }))
                  }
                  className="relative h-full w-full object-cover transition-opacity duration-700 !rounded-2xl"
                  style={{ opacity: imgLoaded[s.image] ? 1 : 0 }}
                />
              </div>
            ))}
            {/* Caption at bottom */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent p-6 pr-24 md:p-12 md:pr-28">
              <p
                key={`caption-${index}`}
                className="font-serif text-sm italic text-white opacity-0 animate-[fadeSlide_0.7s_ease-out_0.4s_forwards] md:text-base"
              >
                — {active.imageCaption}
              </p>
            </div>
            {/* MESOFT tag top-right */}
            <div
              key={`tag-${index}`}
              className="absolute right-4 top-4 rounded-full px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-white opacity-0 animate-[fadeSlide_0.7s_ease-out_0.6s_forwards] md:right-6 md:top-6"
              style={{ background: active.accent }}>
              {/*{t("heroSlider.common.brandTag")} · */}
              {active.number}
            </div>
            {/* === Prev / Next arrow buttons overlaid on the image === */}
            <div className="absolute inset-y-0 left-0 flex items-center">
              <button
                onClick={prev}
                aria-label={t("heroSlider.common.previousSlide")}
                className="ml-3 flex h-11 w-11 items-center justify-center rounded-full bg-white/85 text-white backdrop-blur-md transition-all hover:bg-white hover:scale-110 hover:shadow-lg md:ml-4 md:h-14 md:w-14"
                style={{ background: active.accent }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m12 19-7-7 7-7" />
                  <path d="M19 12H5" />
                </svg>
              </button>
            </div>
            <div className="absolute inset-y-0 right-0 flex items-center">
              <button
                onClick={next}
                aria-label={t("heroSlider.common.nextSlide")}
                className="mr-3 flex h-11 w-11 items-center justify-center rounded-full text-white backdrop-blur-md transition-all hover:scale-110 hover:shadow-lg md:mr-4 md:h-14 md:w-14"
                style={{ background: active.accent }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14" />
                  <path d="m12 5 7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Bottom controls */}
      <div className=" relative border-t border-stone-300 bg-slate-50 hidden xl:flex">
        <div className="md:w-theme-container w-mobile-container mx-auto flex broke-max-w-[1400px] items-center justify-between gap-4 py-5 ">
          {/* Thumbnails */}
          <div className="flex items-center gap-1 md:gap-2">
            {slides.map((s, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className="group relative h-14 w-20 overflow-hidden rounded-sm transition-all md:w-24"
                style={{
                  opacity: i === index ? 1 : 0.4,
                  outline: i === index ? `2px solid ${s.accent}` : "none",
                  outlineOffset: "2px",
                }}
                aria-label={t("heroSlider.common.goToSlide", {
                  number: i + 1,
                  category: s.category,
                })}>
                <img
                  src={s.imageBlur}
                  alt=""
                  aria-hidden="true"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 flex items-end justify-start bg-gradient-to-t from-black/70 to-transparent p-2">
                  <span className="font-serif text-[10px] italic text-white md:text-xs">
                    {s.number}
                  </span>
                </div>
              </button>
            ))}
          </div>
          {/* Progress bar + category label (desktop) */}
          <div className="hidden items-center gap-4 md:flex">
            <div className="h-px w-32 bg-stone-300">
              <div
                key={`bar-${index}-${isPaused}`}
                className="h-full"
                style={{
                  background: active.accent,
                  animation: isPaused ? "none" : "progress 7s linear forwards",
                }}/>
            </div>
            <span className="font-serif text-sm italic text-stone-500">
              {active.category}
            </span>
            {/* Additional prev/next pair next to progress — nice for desktop */}
            <div className="flex items-center gap-1 pl-2">
              <button
                onClick={prev}
                aria-label={t("heroSlider.common.previousSlide")}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-stone-300 text-stone-700 transition-all hover:border-stone-900 hover:bg-stone-900 hover:text-white">
                {/*<ArrowLeft className="h-4 w-4" strokeWidth={1.75} />*/}
              </button>
              <button
                onClick={next}
                aria-label={t("heroSlider.common.nextSlide")}
                className="flex h-9 w-9 items-center justify-center rounded-full text-white transition-all hover:scale-105"
                style={{ background: active.accent }}>
                {/*<ArrowRight className="h-4 w-4" strokeWidth={1.75} />*/}
              </button>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes fadeSlide {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes progress {
          from { width: 0%; }
          to   { width: 100%; }
        }
      `}</style>
    </section>
  );
}
